import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { AlertTriangle, Loader2, Rocket, Save } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/shared/ui/button";

import { websiteService, type HostSlots } from "../api/websiteService";
import { applyEdit, placeholderFacts } from "../model/content";
import { setTheme } from "../model/hostExtras";
import {
  absoluteHostUrl,
  buildNodeTree,
  setNodeValue,
  USER_EDIT,
  type ImageSlot,
} from "../model/hostNodes";
import { HostImagePanel } from "./components/host/HostImagePanel";
import { HostNodeEditor } from "./components/host/HostNodeEditor";
import { HostNodeTree } from "./components/host/HostNodeTree";
import { HostPreflightBlockers, HostPreviewCard } from "./components/host/HostPreviewCard";
import { HostProductsPanel } from "./components/host/HostProductsPanel";
import { HostSlotImagePanel } from "./components/host/HostSlotImagePanel";
import { HostTextsPanel } from "./components/host/HostTextsPanel";
import { HostThemePanel } from "./components/host/HostThemePanel";
import { logoColor } from "./components/host/logoColor";
import { useCanManageSite } from "./hooks/useCanManageSite";
import type { DraftEditor } from "./hooks/useDraftEditor";
import { useHostPublish } from "./hooks/useHostPublish";
import { SiteShell } from "./SiteShell";

/**
 * 屏 4「内容编辑」· host 模式(宿主 site.xbwhynot.com,A.55 / A.56 / A.57)。`EditPage` 按 `isHostSite` 分派过来,
 * 编辑器状态(函数式写、保存竞态、离开守卫、防串台)与 stub 五屏共用 `useDraftEditor`。
 *
 * 左 = 节点树(页 → 块 → 节点;页序取模板画像 `profileSummary.pages`,块 / 字段由 `buildNodeTree` 稳定排序);右 = 站点卡(替代草稿预览)+ 发布阻断 + 节点编辑 + 图片区。
 * ★ 保存 = 整份 PUT content(改过的节点 binding 置 `user:edit`,A.56)。
 * ★ 发布 = 先存 → preflight → errors 阻断 / warnings 确认 → publish(`useHostPublish`)。STAFF 看不到发布(A.59)。
 * ★ 事实资料还是占位的照旧拦发布并提示回采集(A.2,与 stub 同一条)。
 */
export default function HostEditPage({ ed }: { ed: DraftEditor }) {
  const navigate = useNavigate();
  const canManage = useCanManageSite();
  const { site, template, content, dirty, saving } = ed;
  const hp = useHostPublish(ed);
  const [pageKey, setPageKey] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [slots, setSlots] = useState<HostSlots | null>(null);
  const siteId = site?.id;

  // 1009:模板全部图片位 / 调色板 / 导航与按钮(只读,取不到就不画这三块,不挡编辑)
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    websiteService
      .hostSlots(siteId)
      .then((r) => {
        if (!cancelled) setSlots(r);
      })
      .catch(() => {
        if (!cancelled) setSlots(null);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const pageOrder = template?.profileSummary?.pages;
  const tree = useMemo(
    () => buildNodeTree(content?.transplant, pageOrder),
    [content?.transplant, pageOrder]
  );
  const flagged = useMemo(
    () => new Set(hp.blockers.flatMap((b) => (b.token ? [b.token] : []))),
    [hp.blockers]
  );

  if (!site || !template || !content) return null;
  const id = site.id;
  const collectPath = `/ai-website/sites/${encodeURIComponent(id)}/collect`;

  const guardLink = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty) return;
    e.preventDefault();
    void ed.confirmLeave().then((ok) => {
      if (ok) navigate(to);
    });
  };

  const pageOfToken = (t: string) =>
    tree.find((p) => p.blocks.some((b) => b.nodes.some((n) => n.token === t))) ?? null;
  const page =
    tree.find((p) => p.page === pageKey) ?? (token ? pageOfToken(token) : null) ?? tree[0] ?? null;
  const pageNodes = page?.blocks.flatMap((b) => b.nodes) ?? [];
  const current = pageNodes.find((n) => n.token === token) ?? pageNodes[0] ?? null;

  const locate = (t: string) => {
    const p = pageOfToken(t);
    if (p) setPageKey(p.page);
    setToken(t);
  };

  const nextVersion = (site.currentVersionNo ?? 0) + 1;
  const busy = saving || hp.publishing;
  const pendingFacts = placeholderFacts(template, content);
  const publishBlocked =
    pendingFacts.length > 0
      ? `还有 ${pendingFacts.length} 项资料待补(${[
          ...new Set(pendingFacts.map((f) => f.label)),
        ].join("、")}),补齐后才能发布`
      : undefined;

  const upload = async (slot: ImageSlot, data: string) => {
    const { url } = await websiteService.uploadImage(id, {
      data,
      purpose: slot.purpose,
      ...(slot.productIndex != null ? { productIndex: slot.productIndex } : {}),
    });
    return absoluteHostUrl(url);
  };

  const uploadSlot = async (slotToken: string, data: string) => {
    const { url } = await websiteService.uploadImage(id, {
      data,
      purpose: "slot",
      token: slotToken,
    });
    return absoluteHostUrl(url);
  };

  const uploadProduct = async (productIndex: number, data: string) => {
    const { url } = await websiteService.uploadImage(id, {
      data,
      purpose: "product",
      productIndex,
    });
    return absoluteHostUrl(url);
  };

  const logo = typeof content.brand.logo === "string" ? content.brand.logo.trim() : "";

  return (
    <SiteShell
      site={site}
      pageTitle="内容编辑"
      beforeLeave={ed.confirmLeave}
      subtitle={`v${nextVersion} 草稿 · 未发布 · ${dirty ? "未保存" : "已保存"}${
        site.currentVersionNo != null ? ` · 线上仍是 v${site.currentVersionNo}` : ""
      }`}
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            disabled={!dirty || busy}
            onClick={() => void ed.persist()}
          >
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            保存草稿
          </Button>
          {canManage && (
            <Button
              size="sm"
              disabled={busy || publishBlocked !== undefined}
              title={publishBlocked}
              onClick={() => void hp.publish()}
            >
              {hp.publishing ? <Loader2 className="animate-spin" /> : <Rocket />}
              发布 v{nextVersion}
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[400px_minmax(0,1fr)] xl:items-start">
        <div className="space-y-4">
          {pendingFacts.length > 0 && (
            <p
              role="note"
              className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              <span>
                有 {pendingFacts.length} 项资料待补,请
                <Link
                  to={collectPath}
                  onClick={guardLink(collectPath)}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  回到资料采集补齐
                </Link>
              </span>
            </p>
          )}
          <HostNodeTree
            tree={tree}
            page={page}
            activeToken={current?.token ?? null}
            flagged={flagged}
            onSelectPage={(p) => {
              setPageKey(p);
              setToken(null);
            }}
            onSelectNode={setToken}
          />
        </div>

        <div className="min-w-0 space-y-4">
          <HostPreviewCard site={site} />
          <HostPreflightBlockers errors={hp.blockers} onLocate={locate} />
          {current && (
            <HostNodeEditor
              key={current.token}
              token={current.token}
              node={current.node}
              collectPath={collectPath}
              onCollectClick={guardLink(collectPath)}
              onChange={(v) => ed.edit((prev) => setNodeValue(prev, current.token, v))}
              onRewrite={async (instruction, cur) => {
                const value = await ed.rewriteToken(current.token, instruction, cur);
                if (value === null) return false;
                ed.edit((prev) => setNodeValue(prev, current.token, value));
                return true;
              }}
            />
          )}
          <HostImagePanel
            content={content}
            disabled={busy}
            upload={upload}
            onSet={(path, url) => ed.edit((prev) => applyEdit(prev, path, url, USER_EDIT))}
          />
          {slots && (
            <>
              <HostSlotImagePanel
                content={content}
                slots={slots.images}
                pageOrder={pageOrder}
                disabled={busy}
                upload={uploadSlot}
                onChange={(update) => ed.edit(update)}
              />
              <HostThemePanel
                palette={slots.palette}
                theme={content.theme}
                hasLogo={logo !== ""}
                disabled={busy}
                onChange={(theme) => ed.edit((prev) => setTheme(prev, theme))}
                pickLogoColor={() => logoColor(absoluteHostUrl(logo))}
              />
              <HostTextsPanel
                content={content}
                slots={slots.texts}
                disabled={busy}
                onChange={(update) => ed.edit(update)}
              />
            </>
          )}
          <HostProductsPanel
            content={content}
            disabled={busy}
            upload={uploadProduct}
            onChange={(update) => ed.edit(update)}
          />
        </div>
      </div>
    </SiteShell>
  );
}
